import React, { useCallback, useState } from 'react';

import { Box, Icon, Text } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { usePaymentContext } from 'features/payment';
import { useIntl } from 'react-intl';

import ActionButton from 'components/action-button';
import { CardModal } from 'components/card-modal';
import { CurrentSelectedMethodType } from 'components/payment-method/types';
import { useLoyaltyUpdateIdAndPayMutation } from 'generated/graphql-gateway';
import useErrorModal from 'hooks/use-error-modal';
import { AddGiftCardInput } from 'pages/cart/payment/add-gift-card-input';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useUIContext } from 'state/ui';
import { theme } from 'styles/configure-theme';
import { primitive } from 'styles/constants/primitives';
import logger from 'utils/logger';

import GiftCardImage from '../gift-card-image';
import { useCardNumber } from '../use-giftcard-number';

const GIFT_CARD_NUMBER_TRANSLATION_ID = 'giftCardNumber';

export const AddGiftCardModal: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    defaultPaymentMethodId,
    getPrepaidPaymentMethod,
    mergePrepaidCardBalances,
    setCheckoutPaymentMethodId,
  } = usePaymentContext();
  const { formatMessage } = useIntl();
  const { giftCardNumber, errors, isValidGiftCardNumber, handleCardNumberChange } = useCardNumber();
  const { formatCurrencyForUser } = useUIContext();
  const { loyaltyUser } = useLoyaltyUser();
  const [loyaltyQuickPayMutation] = useLoyaltyUpdateIdAndPayMutation();
  const router = useRouter();
  const [successfulAddAmount, setSuccessfulAddAmount] = useState<string | null>(null);

  const handleCloseModal = () => {
    setCheckoutPaymentMethodId(defaultPaymentMethodId || CurrentSelectedMethodType.ADD_NEW_CARD);
    router.dismiss();
  };

  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Vaulting Gift Card',
    onCancel: handleCloseModal,
  });

  const addNewCard = useCallback(
    async (event: any) => {
      event.preventDefault();

      setIsLoading(true);

      if (getPrepaidPaymentMethod()) {
        return openErrorDialog({ message: formatMessage({ id: 'giftCardExists' }) });
      }

      try {
        const { prepaidDetails, storedPaymentMethodId } = await mergePrepaidCardBalances({
          // Source pin is not required, BK crown cards don't have it
          sourceCardNumber: giftCardNumber,
        });
        loyaltyQuickPayMutation({
          variables: {
            idAndPayInput: { storedPaymentMethodId, enabled: true },
            loyaltyId: loyaltyUser?.id,
          },
        });

        setCheckoutPaymentMethodId(storedPaymentMethodId);
        setSuccessfulAddAmount(formatCurrencyForUser(prepaidDetails.balance));
      } catch (error) {
        // @ts-expect-error TS(2322) FIXME: Type 'unknown' is not assignable to type 'Error | ... Remove this comment to see the full error message
        openErrorDialog({ message: formatMessage({ id: 'giftCardAddingError' }), error });
        logger.error({ error, message: 'Error adding gift card' });
      } finally {
        setIsLoading(false);
      }
    },
    [
      getPrepaidPaymentMethod,
      openErrorDialog,
      formatMessage,
      mergePrepaidCardBalances,
      giftCardNumber,
      loyaltyQuickPayMutation,
      loyaltyUser?.id,
      setCheckoutPaymentMethodId,
      formatCurrencyForUser,
    ]
  );

  const submitButtonText = `${formatMessage({ id: 'save' })} & ${formatMessage({
    id: 'continue',
  })}`;

  const cardAlias = formatMessage({ id: 'cardAlias.bk' });
  const yourNewCardBalance = formatMessage(
    { id: 'yourNewCardBalance' },
    { cardAlias, amount: successfulAddAmount }
  );

  return (
    <CardModal
      analyticsEvent={successfulAddAmount ? 'Success gift card' : 'Add gift card'}
      testID="add-giftcard-form"
      onDismiss={handleCloseModal}
      standardPadding
      headerText={
        successfulAddAmount
          ? formatMessage({ id: 'success' })
          : formatMessage({ id: 'addNewPaymentMethodGiftCard' })
      }
      footer={
        successfulAddAmount ? (
          <ActionButton fullWidth testID="close-now" onPress={() => router.dismiss()}>
            {formatMessage({ id: 'close' })}
          </ActionButton>
        ) : (
          <ActionButton
            fullWidth
            testID="save-and-continue"
            isLoading={isLoading}
            disabled={!isValidGiftCardNumber}
            onPress={addNewCard}
          >
            {submitButtonText}
          </ActionButton>
        )
      }
    >
      {successfulAddAmount ? (
        <>
          <SuccessIcon />
          <Box
            bgColor={primitive.$blackOpacity10}
            flexDirection="row"
            width="full"
            paddingY="$3"
            paddingX="$6"
            alignItems="center"
          >
            <Icon variant="infoFill" size="$5" color={theme.token('icon-default')} />
            <Info>{yourNewCardBalance}</Info>
          </Box>
        </>
      ) : (
        <>
          <GiftCardImage />
          <AddGiftCardInput
            onChange={handleCardNumberChange}
            errorMessage={errors?.giftCardNumber}
            label={formatMessage({ id: GIFT_CARD_NUMBER_TRANSLATION_ID })}
            testID="add-giftcard-input"
          />
        </>
      )}
      <ErrorDialog />
    </CardModal>
  );
};

export const Info = Text.withConfig({
  marginY: '$0',
  marginRight: '$0',
  marginLeft: '$4',
  variant: 'copyOne',
});

export const SuccessIcon = Icon.withConfig({
  variant: 'bkCrown',
  size: '$12',
  alignSelf: 'center',
  color: theme.token('crown-color'),
});
